import Box from '@mui/material/Box';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import {
  type SearchAndFiltersConfig,
  SearchAndFiltersPanel,
} from '@openx/components-core/lib/SelectionDrawer/components/SearchAndFiltersPanel.tsx';
import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext.tsx';
import { FiltersBar } from '@openx/components-filters/lib/FiltersBar/FiltersBar.tsx';
import { type ConfigurableFilter, FILTER_TYPES } from '@openx/components-filters/lib/FiltersBar/types.ts';
import type { Option } from '@openx/types/options.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTEXTUAL_SEGMENT_SOURCES,
  type ContextualSegmentFilters,
  type ContextualSegmentSource,
} from '../hooks/useContextualSegmentFilters.ts';

const FILTER_KEYS = {
  DataPartner: 'dataPartner',
  Price: 'price',
} as const;

type ContextualSegmentsSearchAndFiltersProps = {
  phrase: string;
  onPhraseChange: (value: string) => void;
  source: ContextualSegmentSource;
  onSourceChange: (value: ContextualSegmentSource) => void;
  filters: ContextualSegmentFilters;
  onFiltersChange: (value: ContextualSegmentFilters) => void;
  dataPartnerOptions: Option[];
  dataPartnerOptionsLoading: boolean;
  hasOwnSegments: boolean;
  hasActiveFilters: boolean;
  onClearAll: () => void;
  showBulkAdd: boolean;
  isBulkAddDisabled: boolean;
  onBulkAdd: () => void;
};

export const ContextualSegmentsSearchAndFilters = ({
  phrase,
  onPhraseChange,
  source,
  onSourceChange,
  filters,
  onFiltersChange,
  dataPartnerOptions,
  dataPartnerOptionsLoading,
  hasOwnSegments,
  hasActiveFilters,
  onClearAll,
  showBulkAdd,
  isBulkAddDisabled,
  onBulkAdd,
}: ContextualSegmentsSearchAndFiltersProps) => {
  const { t } = useTranslation();
  const filtersConfig = useMemo<ConfigurableFilter<keyof ContextualSegmentFilters>[]>(
    () => [
      {
        key: FILTER_KEYS.DataPartner,
        name: t('Data Partner'),
        type: FILTER_TYPES.AUTOCOMPLETE,
        options: dataPartnerOptions,
        withLoader: dataPartnerOptionsLoading,
        config: {
          selectedPlaceholder: t('[ Select Data Partner ]'),
          dropdownHeight: '220px',
          isLoading: dataPartnerOptionsLoading,
        },
      },
      {
        key: FILTER_KEYS.Price,
        name: t('Price'),
        type: FILTER_TYPES.MULTI_OPTION_WITH_RANGE,
        options: [
          { id: 'pricePom', name: t('Percent of Media') },
          { id: 'priceCpm', name: t('Fixed CPM') },
        ],
        config: {
          inputType: 'float',
          precision: 2,
          debounce: 400,
          detailedOptionsConfig: {
            minFieldPlaceholder: t('Enter Min. value'),
            maxFieldPlaceholder: t('Enter Max. value'),
            multiOptionsConfig: [{ endAdornment: '%', minValue: 0, maxValue: 100 }, { endAdornment: '$' }],
          },
        },
      },
    ],
    [dataPartnerOptions, dataPartnerOptionsLoading, t]
  );
  const filterProviderConfig = useMemo(
    () => ({
      filtersConfig,
      visibleFilters: [FILTER_KEYS.DataPartner, FILTER_KEYS.Price],
      criteria: { filters },
      disabledFilters: source === CONTEXTUAL_SEGMENT_SOURCES.My ? [FILTER_KEYS.DataPartner] : [],
      hideClearFiltersButton: true,
      loadingFilters: dataPartnerOptionsLoading ? [FILTER_KEYS.DataPartner] : [],
      onFiltersChange,
    }),
    [dataPartnerOptionsLoading, filters, filtersConfig, onFiltersChange, source]
  );
  const searchAndFiltersConfig = useMemo<SearchAndFiltersConfig>(
    () => ({
      search: {
        value: phrase,
        onChange: onPhraseChange,
        placeholder: t('Search for Segments'),
        dataTest: 'contextual-segments-search',
      },
      prefixFilter: {
        options: [
          { value: CONTEXTUAL_SEGMENT_SOURCES.All, label: t('All Segments') },
          {
            value: CONTEXTUAL_SEGMENT_SOURCES.My,
            label: t('My Segments'),
            disabled: !hasOwnSegments,
          },
        ],
        value: source,
        onChange: value => onSourceChange(value as ContextualSegmentSource),
        ariaLabel: t('Segment source'),
        dataTest: 'contextual-segments-source',
      },
      filters: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            <FiltersContextProvider key={source} config={filterProviderConfig}>
              <FiltersBar />
            </FiltersContextProvider>
            {hasActiveFilters ? (
              <Button variant="text" onClick={onClearAll} sx={{ color: 'error.main', flexShrink: 0 }}>
                {t('Clear filters')}
              </Button>
            ) : null}
          </Box>
          {showBulkAdd ? (
            <Button
              onClick={onBulkAdd}
              data-test="bulk-add-button"
              disabled={isBulkAddDisabled}
              sx={{ flexShrink: 0, mr: 2 }}
            >
              {t('Bulk Add')}
            </Button>
          ) : null}
        </Box>
      ),
    }),
    [
      filterProviderConfig,
      hasOwnSegments,
      hasActiveFilters,
      isBulkAddDisabled,
      onBulkAdd,
      onClearAll,
      onPhraseChange,
      onSourceChange,
      phrase,
      showBulkAdd,
      source,
      t,
    ]
  );

  return <SearchAndFiltersPanel config={searchAndFiltersConfig} />;
};
